import React, { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from 'components/ui/Modal'
import { Input } from 'components/ui/Input'
import { Textarea } from 'components/ui/Textarea'
import { Button } from 'components/ui/Button'
import { FormField } from 'components/ui/FormField'
import { ModernDatePicker } from 'components/ui/ModernDatePicker'
import { useToast } from 'contexts/ToastContext'
import { type Candidate, type Application, interviewsApi } from 'lib/api'

interface Step {
  id: number
  name: string
  order: number
  color?: string
  step_type?: string // Optional for backward compatibility
}

interface ScheduleInterviewModalProps {
  isOpen: boolean
  onClose: () => void
  candidate: Candidate
  application?: Application
  offerId?: number
  steps?: Step[]
  defaultStepOrder?: number
  existingInterview?: {
    id: number | string
    title: string
    start: Date
    end: Date
    candidateId: number
    candidateName: string
    applicationId?: number
    stepOrder?: number
    stepName?: string
    location?: string
    meetingLink?: string
    notes?: string
    interviewerName?: string
    duration?: number
  }
  onSuccess?: (interview: {
    id: number | string
    title: string
    start: Date
    end: Date
    candidateId: number
    candidateName: string
    applicationId?: number
    stepOrder?: number
    stepName?: string
    location?: string
    meetingLink?: string
    notes?: string
  }) => void
}

export function ScheduleInterviewModal({
  isOpen,
  onClose,
  candidate,
  application,
  offerId,
  steps = [],
  defaultStepOrder,
  existingInterview,
  onSuccess,
}: ScheduleInterviewModalProps) {
  const { addToast } = useToast()
  const { t } = useTranslation()

  // Step order is always set from defaultStepOrder (cannot be changed)
  const stepOrder = defaultStepOrder !== undefined ? defaultStepOrder : null
  const selectedStep = stepOrder !== null ? steps.find(s => s.order === stepOrder) : undefined

  const [title, setTitle] = useState('')
  const [scheduledFor, setScheduledFor] = useState<Date | null>(null)
  const [duration, setDuration] = useState(30)
  const [location, setLocation] = useState('')
  const [meetingLink, setMeetingLink] = useState('')
  const [notes, setNotes] = useState('')
  const [interviewerName, setInterviewerName] = useState('')
  const [errors, setErrors] = useState<{ scheduledFor?: string; title?: string }>({})
  const [saving, setSaving] = useState(false)
  const previousCandidateIdRef = useRef<number | null>(null)

  useEffect(() => {
    // Only reset form when modal opens with a different candidate or opens fresh
    const candidateChanged = previousCandidateIdRef.current !== candidate?.id
    
    if (isOpen && candidate) {
      // If there's an existing interview, load its data (editing mode)
      if (existingInterview) {
        setTitle(existingInterview.title || '')
        setScheduledFor(existingInterview.start ? new Date(existingInterview.start) : null)
        setDuration(existingInterview.duration || 30)
        setLocation(existingInterview.location || '')
        setMeetingLink(existingInterview.meetingLink || '')
        setNotes(existingInterview.notes || '')
        setInterviewerName(existingInterview.interviewerName || '')
        setErrors({})
        previousCandidateIdRef.current = candidate.id
      }
      // Initialize form only if candidate changed or modal just opened (new interview)
      else if (candidateChanged || !previousCandidateIdRef.current) {
        const stepForTitle = stepOrder !== null 
          ? steps.find(s => s.order === stepOrder)
          : undefined
        
        const defaultTitle = stepForTitle
          ? t('offerDetail.scheduleInterview.defaultTitleWithStep', {
              name: candidate.name,
              step: stepForTitle.name,
              defaultValue: `Interview ${stepForTitle.name} - ${candidate.name}`,
            })
          : t('offerDetail.scheduleInterview.defaultTitle', {
              name: candidate.name,
              defaultValue: `Interview - ${candidate.name}`,
            })
        setTitle(defaultTitle)
        // Set default time: if it's late (after 6 PM), use tomorrow at 9:00 AM, otherwise today at 9:00 AM
        const now = new Date()
        const defaultDate = new Date()
        const currentHour = now.getHours()
        const currentMinute = now.getMinutes()
        
        // If it's after 6 PM (18:00), schedule for tomorrow at 9 AM
        if (currentHour >= 18) {
          defaultDate.setDate(now.getDate() + 1)
          defaultDate.setHours(9, 0, 0, 0)
        } else if (currentHour < 9) {
          // If it's before 9 AM today, use today at 9 AM
          defaultDate.setHours(9, 0, 0, 0)
        } else {
          // If it's between 9 AM and 6 PM, use today at 9 AM (default working hour)
          // User can change it if they want a different time
          defaultDate.setHours(9, 0, 0, 0)
        }
        setScheduledFor(defaultDate)
        setDuration(30)
        setLocation('')
        setMeetingLink('')
        setNotes('')
        setInterviewerName('')
        setErrors({})
        previousCandidateIdRef.current = candidate.id
      }
    }
    
    // Reset when modal closes
    if (!isOpen) {
      previousCandidateIdRef.current = null
    }
  }, [isOpen, candidate, t, stepOrder, steps, existingInterview])

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    // Validation: Check if step is valid for interview creation
    // Interviews can only be created from step 3 onwards, but not for "rejected" step
    if (stepOrder !== null && stepOrder !== undefined) {
      if (stepOrder < 3) {
        addToast({
          type: 'error',
          title: t('common.error', 'Error'),
          message: t('offerDetail.scheduleInterview.validation.stepTooEarly', 
            'Interviews can only be scheduled starting from step 3.'),
        })
        return
      }
      
      // Check if step is "rejected" by step_type or name
      const isRejected = selectedStep?.step_type === 'rejected' || 
                        selectedStep?.name?.toLowerCase().includes('rejected') ||
                        selectedStep?.name?.toLowerCase().includes('reject')
      
      if (isRejected) {
        addToast({
          type: 'error',
          title: t('common.error', 'Error'),
          message: t('offerDetail.scheduleInterview.validation.rejectedStep', 
            'Interviews cannot be scheduled for rejected candidates.'),
        })
        return
      }
    }

    // Validation
    const newErrors: { scheduledFor?: string; title?: string } = {}
    if (!scheduledFor) {
      newErrors.scheduledFor = t(
        'offerDetail.scheduleInterview.validation.dateRequired',
        'Date and time are required'
      )
    }
    if (!title.trim()) {
      newErrors.title = t('offerDetail.scheduleInterview.validation.titleRequired', 'Title is required')
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setSaving(true)
    try {
      const startDate = new Date(scheduledFor!)
      const endDate = new Date(startDate.getTime() + duration * 60000)

      // Call API to save or update interview
      if (!offerId) {
        throw new Error('Offer ID is required')
      }

      let savedInterview
      // Only update if there's an existing interview for the SAME step
      // Otherwise, always create a new interview to maintain full traceability
      if (existingInterview && typeof existingInterview.id === 'number' && 
          existingInterview.stepOrder === stepOrder) {
        // Update existing interview ONLY if it's for the same step
        const response = await interviewsApi.update(existingInterview.id, {
          title: title.trim(),
          scheduled_at: startDate.toISOString(),
          duration_minutes: duration,
          location: location.trim() || null,
          meeting_link: meetingLink.trim() || null,
          notes: notes.trim() || null,
          interviewer_name: interviewerName.trim() || null,
          step_order: stepOrder !== null && stepOrder !== undefined ? stepOrder : null,
          step_name: selectedStep?.name || null,
        })
        savedInterview = response.data
      } else {
        // Always create a new interview if:
        // 1. No existing interview
        // 2. Existing interview is for a different step (maintain traceability)
        // This ensures all interviews are preserved in the database
        const response = await interviewsApi.create({
          candidate: candidate.id,
          application: application?.id || null,
          offer: offerId,
          title: title.trim(),
          scheduled_at: startDate.toISOString(),
          duration_minutes: duration,
          location: location.trim() || null,
          meeting_link: meetingLink.trim() || null,
          notes: notes.trim() || null,
          interviewer_name: interviewerName.trim() || null,
          step_order: stepOrder !== null && stepOrder !== undefined ? stepOrder : null,
          step_name: selectedStep?.name || null,
        })
        savedInterview = response.data
      }

      // Create interview object for the calendar (matching InterviewEvent format)
      const interview = {
        id: savedInterview.id,
        title: savedInterview.title,
        start: new Date(savedInterview.scheduled_at),
        end: new Date(savedInterview.end_time),
        candidateId: savedInterview.candidate_id,
        candidateName: savedInterview.candidate_name,
        applicationId: savedInterview.application_id || undefined,
        stepOrder: savedInterview.step_order || undefined,
        stepName: savedInterview.step_name || undefined,
        location: savedInterview.location || undefined,
        meetingLink: savedInterview.meeting_link || undefined,
        notes: savedInterview.notes || undefined,
        interviewerName: savedInterview.interviewer_name || undefined,
      }

      addToast({
        type: 'success',
        title: t('offerDetail.scheduleInterview.successTitle', 'Interview Scheduled'),
        message: t('offerDetail.scheduleInterview.successMessage', 'Interview has been scheduled successfully.'),
        duration: 5000,
      })

      // Reset form on success before closing
      setTitle('')
      setScheduledFor(null)
      setDuration(30)
      setLocation('')
      setMeetingLink('')
      setNotes('')
      setInterviewerName('')
      setErrors({})
      previousCandidateIdRef.current = null
      
      onSuccess?.(interview)
      onClose()
    } catch (error: any) {
      console.error('Error scheduling interview:', error)
      
      // Extract error message from different possible formats
      let errorMessage = t('offerDetail.scheduleInterview.errorMessage', 'Failed to schedule interview. Please try again.')
      
      if (error.response?.data) {
        const errorData = error.response.data
        
        // Try different error formats
        if (errorData.non_field_errors && Array.isArray(errorData.non_field_errors)) {
          errorMessage = errorData.non_field_errors[0]
        } else if (errorData.detail) {
          errorMessage = errorData.detail
        } else if (typeof errorData === 'string') {
          errorMessage = errorData
        } else if (errorData.message) {
          errorMessage = errorData.message
        } else if (Array.isArray(errorData) && errorData.length > 0) {
          errorMessage = errorData[0]
        } else {
          // Try to get first error value from object
          const errorKeys = Object.keys(errorData)
          if (errorKeys.length > 0) {
            const firstError = errorData[errorKeys[0]]
            if (Array.isArray(firstError)) {
              errorMessage = firstError[0]
            } else if (typeof firstError === 'string') {
              errorMessage = firstError
            }
          }
        }
      } else if (error.message) {
        errorMessage = error.message
      }
      
      addToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: errorMessage,
      })
    } finally {
      setSaving(false)
    }
  }

  // Check if existing interview is for a different step (we'll create new one)
  const isCreatingNewForDifferentStep = existingInterview && 
    stepOrder !== null && 
    existingInterview.stepOrder !== undefined && 
    existingInterview.stepOrder !== stepOrder

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        existingInterview && !isCreatingNewForDifferentStep
          ? t('offerDetail.scheduleInterview.editTitle', 'Edit Interview')
          : isCreatingNewForDifferentStep
          ? t('offerDetail.scheduleInterview.newInterviewTitle', 'Schedule New Interview')
          : t('offerDetail.scheduleInterview.title', 'Schedule Interview')
      }
      size="lg"
      footer={
        <div className="flex flex-col-reverse sm:flex-row sm:justify-between sm:items-center gap-3 w-full">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button 
            type="submit" 
            form="schedule-interview-form"
            loading={saving} 
            className="w-full sm:w-auto"
          >
            {t('offerDetail.scheduleInterview.submit', 'Schedule Interview')}
          </Button>
        </div>
      }
    >
      <form id="schedule-interview-form" onSubmit={handleSubmit} className="space-y-6">
        {/* Info message if creating new interview for different step */}
        {isCreatingNewForDifferentStep && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <p className="text-sm text-blue-900 dark:text-blue-100">
              {t('offerDetail.scheduleInterview.newInterviewInfo', 
                'You are scheduling a new interview for this step. Previous interviews will be preserved for full traceability.')}
            </p>
          </div>
        )}

        {/* Calendly helper info temporarily disabled */}
        {/* {!existingInterview && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
            <p className="text-sm text-green-900 dark:text-green-100">
              <strong>{t('offerDetail.scheduleInterview.calendlyInfoTitle', '📅 Calendly Scheduled Interview?')}</strong>
            </p>
            <p className="text-sm text-green-800 dark:text-green-200 mt-1">
              {t('offerDetail.scheduleInterview.calendlyInfoMessage', 
                'If the candidate scheduled this interview via Calendly, please enter the date and time they selected below. This interview will then appear in your calendar.')}
            </p>
          </div>
        )} */}
        
        <div className="grid gap-4">
          {steps.length > 0 && defaultStepOrder !== undefined && (
            <FormField 
              label={t('offerDetail.scheduleInterview.step', 'Étape')} 
              required
            >
              <Input
                value={selectedStep ? `${selectedStep.order}. ${selectedStep.name}` : ''}
                disabled
                readOnly
                className="bg-gray-50 dark:bg-gray-800 cursor-not-allowed"
              />
            </FormField>
          )}

          <FormField label={t('offerDetail.scheduleInterview.interviewTitle', 'Interview Title')} required error={errors.title}>
            <Input
              value={title}
              onChange={(event) => {
                setTitle(event.target.value)
                setErrors({ ...errors, title: undefined })
              }}
              required
              placeholder={t('offerDetail.scheduleInterview.titlePlaceholder', 'e.g., Technical Interview')}
            />
          </FormField>

          <FormField
            label={t('offerDetail.scheduleInterview.dateTime', 'Date & Time')}
            required
            error={errors.scheduledFor}
          >
            <ModernDatePicker
              selected={scheduledFor}
              onChange={(date) => {
                setScheduledFor(date)
                setErrors({ ...errors, scheduledFor: undefined })
              }}
              placeholder={t('offerDetail.scheduleInterview.datePlaceholder', 'Select date and time')}
              showTimeSelect
              minDate={new Date()}
            />
          </FormField>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label={t('offerDetail.scheduleInterview.duration', 'Duration (minutes)')} required>
              <Input
                type="number"
                min={10}
                step={5}
                value={duration}
                onChange={(event) => setDuration(Number(event.target.value))}
                required
              />
            </FormField>

            <FormField label={t('offerDetail.scheduleInterview.location', 'Location')}>
              <Input
                value={location}
                onChange={(event) => setLocation(event.target.value)}
                placeholder={t('offerDetail.scheduleInterview.locationPlaceholder', 'Office, Zoom, etc.')}
              />
            </FormField>
          </div>

          <FormField label={t('offerDetail.scheduleInterview.meetingLink', 'Meeting Link')}>
            <Input
              type="url"
              value={meetingLink}
              onChange={(event) => setMeetingLink(event.target.value)}
              placeholder={t('offerDetail.scheduleInterview.meetingLinkPlaceholder', 'https://zoom.us/j/...')}
            />
          </FormField>

          <FormField label={t('offerDetail.scheduleInterview.interviewerName', 'Interviewer Name')}>
            <Input
              value={interviewerName}
              onChange={(event) => setInterviewerName(event.target.value)}
              placeholder={t('offerDetail.scheduleInterview.interviewerNamePlaceholder', 'Name of the person conducting the interview')}
            />
          </FormField>

          <FormField label={t('offerDetail.scheduleInterview.notes', 'Notes')}>
            <Textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder={t('offerDetail.scheduleInterview.notesPlaceholder', 'Additional notes...')}
              rows={3}
            />
          </FormField>
        </div>
      </form>
    </Modal>
  )
}

